import { useCollection } from '@cloudscape-design/collection-hooks';
import { Alert, ContentLayout, Link } from '@cloudscape-design/components';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import ButtonDropdown from '@cloudscape-design/components/button-dropdown';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Table from '@cloudscape-design/components/table';
import TextFilter from '@cloudscape-design/components/text-filter';
import { isEmpty } from 'lodash';
import { DateTime } from 'luxon';
import React, { useCallback, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import Layout from '../../../../components/Layout';
import {
  Allergen,
  GetAllergensDocument,
  GetAllergensQuery,
  useDeleteAllergenMutation,
  useGetAllergensQuery,
} from '../../../../generated/graphql';
import useNotifications from '../../../../hooks/useNotifications';
import MetaTags from '../../../../lib/MetaTags';
import Create from './Create';
import Delete from './Delete';
import Edit from './Edit';
import { getMatchesCountText } from './table-config';

type EmptyStateProps = {
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
};
function EmptyState({ title, subtitle, action }: EmptyStateProps) {
  return (
    <Box textAlign="center" color="inherit">
      <Box variant="strong" textAlign="center" color="inherit">
        {title}
      </Box>
      <Box variant="p" padding={{ bottom: 's' }} color="inherit">
        {subtitle}
      </Box>
      {action}
    </Box>
  );
}

const Allergens = () => {
  const [createModal, setCreateModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const { data } = useGetAllergensQuery({});
  const [deleteAllergene] = useDeleteAllergenMutation();

  const {
    notifications,
    notifyDeletedOrUpdated,
    notifyFailed,
    notifyInProgress,
  } = useNotifications();

  const viewData = useMemo(() => {
    if (!data?.listAllergens?.items) return [];
    return data.listAllergens.items.map((item) => {
      return {
        ...item,
        is_active: item?.is_active ? 'Ja' : 'Nein',
        updated_at: item?.updated_at
          ? DateTime.fromSeconds(item.updated_at).toLocaleString(
              DateTime.DATETIME_MED,
            )
          : '-',
      };
    });
  }, [data]);

  const [preferences] = useState({
    contentDisplay: [
      { id: 'id', visible: false },
      { id: 'text', visible: true },
      { id: 'is_active', visible: true },
      { id: 'updated_at', visible: true },
    ],
  });

  const {
    items,
    actions,
    filteredItemsCount,
    collectionProps,
    filterProps,
    actions: { setSelectedItems },
  } = useCollection(viewData, {
    filtering: {
      empty: (
        <EmptyState
          title="Keine Einträge gefunden"
          action={
            <Button onClick={() => setCreateModal(true)}>Erstellen</Button>
          }
        />
      ),
      noMatch: (
        <EmptyState
          title="Keine Einträge gefunden"
          action={
            <Button onClick={() => actions.setFiltering('')}>
              Filter zurücksetzen
            </Button>
          }
        />
      ),
    },
    sorting: {},
    selection: {},
  });
  const { selectedItems, onSelectionChange } = collectionProps;

  const onDeleteDiscard = () => setDeleteModal(false);
  const onDeleteConfirm = useCallback(async () => {
    setSelectedItems([]);
    if (selectedItems && selectedItems.length > 0 && selectedItems[0]) {
      const selectedItem = selectedItems[0];
      const messageId = uuidv4();
      notifyInProgress({ id: messageId, header: 'Eintrag wird gelöscht...' });
      setDeleteModal(false);
      deleteAllergene({
        variables: { input: { id: selectedItem.id ?? '' } },
        update: (cache) => {
          const existingData = cache.readQuery({
            query: GetAllergensDocument,
          }) as GetAllergensQuery;
          const oldData = existingData.listAllergens?.items as Allergen[];
          const newData = oldData.filter((item) => item.id !== selectedItem.id);
          cache.writeQuery({
            query: GetAllergensDocument,
            data: {
              listAllergens: { items: newData },
            },
          });
        },
      })
        .then(async (data) => {
          const result = data.data?.deleteAllergen;
          if (result) {
            notifyInProgress();
            notifyDeletedOrUpdated({
              id: messageId,
              type: 'success',
              content: `Eintrag mit dem Namen "${selectedItem.text}" wurde gelöscht.`,
              header: 'Gelöscht',
              dismissible: true,
            });
          }
        })
        .catch((error) => {
          notifyInProgress();
          notifyFailed({
            id: messageId,
            content: JSON.stringify(error),
            header: 'Ein Fehler ist aufgetreten',
            dismissible: true,
          });
        });
    }
  }, [
    deleteAllergene,
    notifyFailed,
    notifyInProgress,
    selectedItems,
    setSelectedItems,
    notifyDeletedOrUpdated,
  ]);

  return (
    <Layout
      notifications={notifications}
      breadcrumbs={[
        {
          text: 'Webseite',
          href: '/website',
        },
        {
          text: 'Speisekarte',
          href: '/website/food',
        },
        { text: 'Allergene', href: '/website/food/allergens' },
      ]}
    >
      <MetaTags title="Deklarationspflichtige Allergene | Theo's Bistro" />
      <ContentLayout
        header={<Header variant="h1">Deklarationspflichtige Allergene</Header>}
      >
        <SpaceBetween size="m">
          <Alert
            statusIconAriaLabel="Info"
            header="Herkunft der Deklarationspflichtigen Allergenen"
          >
            Diese Liste sollte die Inhalte des{' '}
            <Link
              external
              href="https://www.bmel.de/DE/themen/ernaehrung/lebensmittel-kennzeichnung/pflichtangaben/allergenkennzeichnung.html"
              variant="primary"
            >
              Bundesministerium für Ernährung und Landwirtschaft
            </Link>{' '}
            enthalten.
          </Alert>
          <Table
            {...collectionProps}
            renderAriaLive={({ firstIndex, lastIndex, totalItemsCount }) =>
              `Displaying items ${firstIndex} to ${lastIndex} of ${totalItemsCount}`
            }
            onSelectionChange={onSelectionChange}
            selectedItems={selectedItems}
            stickyColumns={{ first: 1, last: 0 }}
            columnDefinitions={[
              {
                id: 'text',
                header: 'Text',
                cell: (e) => e.text,
                sortingField: 'text',
                isRowHeader: true,
              },
              {
                id: 'is_active',
                header: 'Aktive',
                sortingField: 'is_active',
                cell: (e) => e.is_active,
              },
              {
                id: 'updated_at',
                header: 'Zuletzt bearbeitet',
                cell: (e) => e.updated_at,
                sortingField: 'updated_at',
                isRowHeader: true,
              },
            ]}
            columnDisplay={preferences.contentDisplay}
            enableKeyboardNavigation
            items={items}
            loadingText="Lade ..."
            selectionType="single"
            trackBy="id"
            filter={
              <TextFilter
                {...filterProps}
                countText={getMatchesCountText(filteredItemsCount)}
                filteringPlaceholder="Einträge durchsuchen"
              />
            }
            header={
              <Header
                counter={
                  selectedItems && selectedItems.length
                    ? `(${selectedItems.length}/${viewData.length})`
                    : `(${viewData.length})`
                }
                actions={
                  <SpaceBetween direction="horizontal" size="xs">
                    <ButtonDropdown
                      onItemClick={(event) => {
                        event.preventDefault();
                        setDeleteModal(true);
                      }}
                      disabled={isEmpty(selectedItems)}
                      items={[
                        {
                          id: 'delete',
                          text: 'Löschen',
                          external: false,
                        },
                      ]}
                      mainAction={{
                        disabled: isEmpty(selectedItems),
                        onClick: () => setEditModal(true),
                        text: 'Bearbeiten',
                      }}
                    />
                    <Button
                      variant="primary"
                      onClick={() => setCreateModal(true)}
                    >
                      Erstellen
                    </Button>
                  </SpaceBetween>
                }
              >
                Einträge
              </Header>
            }
          />
        </SpaceBetween>
        <Create
          setVisible={setCreateModal}
          visible={createModal}
          notifyInProgress={notifyInProgress}
          notifyDeletedOrUpdated={notifyDeletedOrUpdated}
          notifyFailed={notifyFailed}
        />
        {selectedItems && selectedItems.length > 0 ? (
          <>
            <Edit
              setVisible={setEditModal}
              visible={editModal}
              selectedId={selectedItems[0].id}
              notifyInProgress={notifyInProgress}
              notifyDeletedOrUpdated={notifyDeletedOrUpdated}
              notifyFailed={notifyFailed}
            />
            <Delete
              visible={deleteModal}
              onDiscard={onDeleteDiscard}
              onDelete={onDeleteConfirm}
              selectedItems={selectedItems}
            />
          </>
        ) : null}
      </ContentLayout>
    </Layout>
  );
};

export default Allergens;
